import { Pick } from 'react-spring';

export const enum PermissionAction {
  view,
  created,
  updated,
  deleted,
}

const ACTION_TO_INDEX: Record<string, number> = {
  view: PermissionAction.view,
  create: PermissionAction.created,
  edit: PermissionAction.updated,
  delete: PermissionAction.deleted,
};

type Profile = {
  email: string;
  name: string;
  profilePicture: string;
  date: string;
};

export type Permission = {
  action: string;
  permissionId: string;
  createdAt: string;
  module: string;
  description: string;
  createdBy: Pick<Profile, 'name'>;
  name: string;
};

type HistoryRecord = {
  userName: string;
  email: string;
  profilePicture: string;
  date: string;
  action: PermissionAction;
};

export type Role = {
  permissions?: Permission[];
  createdAt: string;
  createdBy: Profile;
  salesforceRoleId: string;
  description: string;
  roleId: string;
  roleName: string;
  userCount: number;
  isSystem: boolean;
  isSuperAdmin?: boolean;
  admin?: boolean;
  updatedBy: Profile;
  updatedAt: string;
  history: HistoryRecord[];
};

// Create still only needs roleName (post-create navigates to edit page for full config)
export type CreateRolePayload = Pick<Role, 'roleName' | 'isSuperAdmin' | 'admin'>;

export type UpdateRolePayload = {
  roleName: string;
  salesforceRoleId: string;
  permissions: string[];
  description: string;
  isSuperAdmin: boolean;
  admin: boolean;
};

export interface RolesColumn {
  id: 'roleName' | 'userCount' | 'lastModified' | 'updatedBy' | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
}

export const RolesTableColumns: readonly RolesColumn[] = [
  { id: 'roleName', label: 'Role' },
  { id: 'userCount', label: 'Users assigned' },
  { id: 'lastModified', label: 'Last Modified' },
  { id: 'updatedBy', label: 'Modified By' },
  { id: 'actions', label: '', align: 'right' },
];

export type TableType = {
  id: number;
  columns: {
    name: string;
    value: boolean;
    indeterminate: boolean;
  }[];
  rows: {
    name: string;
    values: boolean[];
  }[];
};

export const syncPermissions = (tables: TableType[], apiPerms: any[]) => {
  // Deep clone to maintain immutability
  const newTables = JSON.parse(JSON.stringify(tables));

  apiPerms.forEach((perm) => {
    // 1. Find the Table (Module)
    const table = newTables.find(
      (t: any) => t.columns[0].name.toLowerCase() === perm.module.toLowerCase(),
    );

    if (table) {
      // 2. Find the Row (Resource)
      // Extracts 'task' from 'read:task'
      const resourceName = perm.name.split(':')[1];
      const row = table.rows.find((r: any) => r.name.toLowerCase() === resourceName.toLowerCase());

      // 3. Update the value at the correct index
      const targetIndex = ACTION_TO_INDEX[perm.action.toLowerCase()];

      if (row && targetIndex !== undefined) {
        row.values[targetIndex] = true;
      }
    }
  });

  return updateHeaderStates(newTables);
};

const updateHeaderStates = (tables: TableType[]) => {
  return tables.map((table) => {
    // Skip the first column (the Table Name column)
    for (let i = 1; i < table.columns.length; i++) {
      const allRowValuesAtCol = table.rows.map((r) => r.values[i - 1]);
      const checkedCount = allRowValuesAtCol.filter(Boolean).length;

      table.columns[i].value = checkedCount === table.rows.length;
      table.columns[i].indeterminate = checkedCount > 0 && checkedCount < table.rows.length;
    }
    return table;
  });
};

const ACTION_MAP: Record<string, number> = {
  view: PermissionAction.view,
  create: PermissionAction.created,
  edit: PermissionAction.updated,
  delete: PermissionAction.deleted,
};

export const mapBackendToTable = (table: TableType, perms: any[]): TableType => {
  const updatedTable = JSON.parse(JSON.stringify(table));

  // Ensure table name exists
  const tableName = updatedTable.columns[0]?.name?.toLowerCase();
  if (!tableName || !perms) return updatedTable;

  perms.forEach((perm) => {
    // 1. Check if perm and required properties exist
    if (!perm?.module || !perm?.name || !perm?.action) {
      console.warn('Skipping invalid permission object:', perm);
      return;
    }

    // 2. Match Module
    if (perm.module.toLowerCase() === tableName) {
      // 3. Handle the "read:task" split safely
      const nameParts = perm.name.split(':');
      if (nameParts.length < 2) return; // Skip if name isn't in 'action:resource' format

      const resourceName = nameParts[1].toLowerCase();

      const rowIndex = updatedTable.rows.findIndex(
        (r: any) => r.name?.toLowerCase() === resourceName,
      );

      // 4. Match Action Index safely
      const actionKey = perm.action.toLowerCase();
      const valIndex = ACTION_MAP[actionKey];

      if (rowIndex !== -1 && valIndex !== undefined) {
        updatedTable.rows[rowIndex].values[valIndex] = true;
      }
    }
  });

  return updatedTable;
};

// Define the map to convert array indices back to action strings
const INDEX_TO_ACTION: Record<number, string> = {
  0: 'view',
  1: 'create',
  2: 'edit',
  3: 'delete',
};

export const mapTableToPermissions = (table: TableType) => {
  const permissions: any[] = [];
  const moduleName = table.columns[0].name; // e.g., "Dashboard"

  table.rows.forEach((row) => {
    row.values.forEach((isEnabled, index) => {
      if (isEnabled) {
        // Construct the backend object
        permissions.push({
          module: moduleName,
          // Reconstruct "read:task" or "write:activity"
          // We assume 'view' is 'read' and others are 'write' based on your previous examples
          name: `${index === 0 ? 'read' : 'write'}:${row.name.toLowerCase()}`,
          action: INDEX_TO_ACTION[index],
        });
      }
    });
  });

  return permissions;
};

export type BatchUpdateRolePayload = {
  emails: string[];
  roleId: string;
};
